import type {
  ClientFeatureFlagName,
  ExperimentName,
  ExperimentParams
} from '@canvas/client-feature-flags';

type MetadataValue = string | number | boolean;

type ReliabilityExperimentDimension = {
  [Name in ExperimentName]: {
    [Param in keyof ExperimentParams[Name]]: {
      kind: 'experiment';
      experimentName: Name;
      paramName: Param;
      deriveValue?: (value: ExperimentParams[Name][Param]) => MetadataValue;
    };
  }[keyof ExperimentParams[Name]];
}[ExperimentName];

export type ReliabilityFeatureDimension =
  | ReliabilityExperimentDimension
  | { kind: 'gate'; featureFlagName: ClientFeatureFlagName };

/**
 * Explicit allowlist for Reliability comparisons. Experiments retain their
 * exp_ cohort fields; gates emit gate_ booleans, not randomized cohorts.
 * Remove entries and dashboard splits when they are no longer useful.
 *
 * Dashboard: https://socrates-workbench-01.cloud.databricks.com/dashboardsv3/01f17bbd1b7012189555285b78c97bbf/published
 */
export const reliabilityFeatureDimensions = [
  { kind: 'gate', featureFlagName: 'databasePerfUplift' },
  { kind: 'gate', featureFlagName: 'cc-dbs-hot-307369-fix' },
  { kind: 'gate', featureFlagName: 'cc-content-types-adaptive-awareness-throttling' },
  { kind: 'gate', featureFlagName: 'cc-content-types-adaptive-persistent-batching' },
  { kind: 'gate', featureFlagName: 'databasesTableLayoutV2' },
  { kind: 'gate', featureFlagName: 'platformTokensFinesse' },
  { kind: 'gate', featureFlagName: 'canvasAgentPresenceFacepileFrontend' },
  {
    kind: 'experiment',
    experimentName: 'databasesViewSpecificOrdering',
    paramName: 'enabled',
    deriveValue: (enabled) => (enabled ? 'test' : 'control')
  },
  {
    kind: 'experiment',
    experimentName: 'databasesSdkLoaderBypass',
    paramName: 'isEnabled',
    deriveValue: (isEnabled) => (isEnabled ? 'test' : 'control')
  },
  {
    kind: 'experiment',
    experimentName: 'databasesViewContextualMenus',
    paramName: 'enabled',
    deriveValue: (enabled) => (enabled ? 'test' : 'control')
  },
  {
    kind: 'experiment',
    experimentName: 'databasesInlineEditingViewName',
    paramName: 'isEnabled',
    deriveValue: (isEnabled) => (isEnabled ? 'test' : 'control')
  },
  {
    kind: 'experiment',
    experimentName: 'databasesContextualMenusM2',
    paramName: 'enabled',
    deriveValue: (enabled) => (enabled ? 'test' : 'control')
  },
  {
    kind: 'experiment',
    experimentName: 'databasesFrontendAiFieldsSummary',
    paramName: 'enabled',
    deriveValue: (enabled) => (enabled ? 'test' : 'control')
  },
  {
    kind: 'experiment',
    experimentName: 'databasesAiFilters',
    paramName: 'isEnabled',
    deriveValue: (isEnabled) => (isEnabled ? 'test' : 'control')
  },
  {
    kind: 'experiment',
    experimentName: 'databaseTableRowHeights',
    paramName: 'enabled',
    deriveValue: (enabled) => (enabled ? 'test' : 'control')
  },
  {
    kind: 'experiment',
    experimentName: 'databasesAsyncSelectSearch',
    paramName: 'isEnabled',
    deriveValue: (isEnabled) => (isEnabled ? 'test' : 'control')
  },
  {
    kind: 'experiment',
    experimentName: 'databasesPinnedLeadingColumns',
    paramName: 'enabled',
    deriveValue: (enabled) => (enabled ? 'test' : 'control')
  },
  {
    kind: 'experiment',
    experimentName: 'databasesFrontendHighlightScrollReliability',
    paramName: 'enabled',
    deriveValue: (enabled) => (enabled ? 'test' : 'control')
  },
  {
    kind: 'experiment',
    experimentName: 'databaseBEStreaming',
    paramName: 'enabled',
    deriveValue: (enabled) => (enabled ? 'test' : 'control')
  }
] satisfies readonly ReliabilityFeatureDimension[];
